import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { AfterViewInit, Component, ElementRef, forwardRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { GbgrundbuchamtService, GrundbuchamtId, GrundbuchamtListEntry, GrundbuchamtResponse } from '../../../core/gb-api';
import { translateName } from '../../functions/language.functions';
import { GbAmtSearchMode } from './gbAmtSearchMode';
import { TranslateNamePipe } from '../../pipes/simple/translate-name.pipe';
import { FormatConfigService } from '../../../core/services/format-config.service';
import { Observable, of, Subscription } from 'rxjs';
import { ComparatorService } from '../../services/comparator-service';
import { AbstractTypeaheadSearch } from '../../components/typeahead-search/abstract-typeahead-search';
import { GbGemeindeSearchMode } from '../gb-gemeinde/gbGemeindeSearchMode';
import { NgbTypeahead } from '@ng-bootstrap/ng-bootstrap';
import { MetaDisablerDirective } from '../../directives/meta-disabler.directive';
import { MetaValidatorDirective } from '../../directives/meta-validator.directive';

@Component({
  selector: 'cap-gb-amt',
  templateUrl: './gb-amt.component.html',
  styleUrls: ['./gb-amt.component.scss'],
  host: {
    class: 'form-control-component'
  },
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => GbAmtComponent), multi: true }],
  imports: [FormsModule, NgbTypeahead, MetaDisablerDirective, MetaValidatorDirective]
})
export class GbAmtComponent
  extends AbstractTypeaheadSearch<GrundbuchamtListEntry>
  implements OnInit, AfterViewInit, OnDestroy, ControlValueAccessor
{
  @Input()
  hasfocus = false;

  @Input()
  searchMode: GbAmtSearchMode | GbGemeindeSearchMode;

  @Input()
  nurGueltige = false;

  @Input()
  placeholder;

  amtModel: GrundbuchamtListEntry;

  minSearchLength: number = 0;

  private reset: boolean = false;
  private amtList: Array<GrundbuchamtListEntry> = [];
  private subscription: Subscription = new Subscription();
  private sortParam: string;

  propagateChange: (gbAmtId: GrundbuchamtId | null) => void = () => {};
  propagateTouch: () => void = () => {};

  @ViewChild('searchInput', { static: false }) searchInput: ElementRef;

  constructor(
    private translate: TranslateService,
    private translateNamePipe: TranslateNamePipe,
    private gbGrundbuchamtService: GbgrundbuchamtService,
    private formatConfigService: FormatConfigService,
    private comparatorService: ComparatorService
  ) {
    super();
  }

  ngOnInit() {
    this.formatConfigService.parameterConfig$.subscribe((config) => {
      this.sortParam = <any>config?.parameters.GRUNDBUCHAMTSORTIERUNGINTERCAPI;
    });
    this.selectOnlyAmt();
  }

  ngAfterViewInit() {
    setTimeout(() => {
      if (this.hasfocus) {
        this.searchInput.nativeElement.focus();
      }
    });
  }

  writeValue(id: GrundbuchamtId | null) {
    if (id) {
      this.subscription.add(
        this.gbGrundbuchamtService.loadGrundbuchamtListEntry(id.id).subscribe((amt) => {
          this.value = translateName(amt.name, this.translate) + ' ' + amt.nummer;
          this.selectedItem = amt;
          this.amtModel = amt;
          this.reset = false;
        })
      );
    } else if (!this.reset) {
      this.onClear();
      this.reset = true;
    }
  }

  registerOnChange(fn: any): void {
    this.propagateChange = fn ?? (() => {});
  }

  registerOnTouched(fn: any): void {
    this.propagateTouch = fn ?? (() => {});
  }

  onSelected($event) {
    const selectedItem: GrundbuchamtListEntry = $event.item;
    if (selectedItem) {
      this.value = this.formatEntry(selectedItem);
      this.amtModel = selectedItem;
      this.propagateChange(selectedItem.id);
      this.reset = false;
    } else {
      this.value = '';
      this.propagateChange(null);
    }
  }

  search = (search$: Observable<string>) => {
    return search$.pipe(
      distinctUntilChanged(),
      switchMap((searchTerm) => {
        this.searchTerm = searchTerm.toLowerCase();
        if (searchTerm?.length >= this.minSearchLength) {
          return this.gbGrundbuchamtService.loadGrundbuchamtAll('RECHTE_REGISTER_SEARCH', this.nurGueltige).pipe(
            map((response) => {
              this.amtList = this.sort(response);
              return this.amtList;
            })
          );
        }
        return of([]);
      }),
      map((data) => {
        const results = data.filter(this.filterResults);
        this.results = results;
        return results;
      })
    );
  };

  public onClear() {
    super.onClear();
    this.amtModel = null;
    this.propagateChange(this.amtModel ? this.amtModel?.id : null);
    this.selectOnlyAmt();
  }

  public selectOnlyAmt() {
    this.gbGrundbuchamtService.loadGrundbuchamtAll('RECHTE_REGISTER_SEARCH', this.nurGueltige).subscribe({
      next: (response: GrundbuchamtResponse) => {
        if (response.grundbuchamtList.length === 1) {
          const amt: GrundbuchamtListEntry = response.grundbuchamtList[0];
          this.value = translateName(amt.name, this.translate) + ' ' + amt.nummer;
          this.selectedItem = amt;
          this.amtModel = amt;
          this.propagateChange(this.amtModel ? this.amtModel.id : null);
          this.reset = false;
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  formatEntry(entry: GrundbuchamtListEntry): string {
    let title: string = this.translateNamePipe.transform(entry.name);
    if (entry.nummer) {
      title += ' ' + entry.nummer;
    }
    return title;
  }

  private sort(response: GrundbuchamtResponse) {
    const list: GrundbuchamtListEntry[] = response.grundbuchamtList;
    switch (this.sortParam) {
      case 'ALPHABETISCH_AUFSTEIGEND':
        return this.sortByComparator(list, (a, b) => this.comparatorService.orderByComparator(a, b));
      case 'ALPHABETISCH_ABSTEIGEND':
        return this.sortByComparator(list, (a, b) => this.comparatorService.orderByComparator(b, a));
      case 'AMTS_NUMMER_AUFSTEIGEND':
        return list.sort((a, b) => this.comparatorService.orderByComparator(a.nummer, b.nummer));
      case 'AMTS_NUMMER_ABSTEIGEND':
        return list.sort((a, b) => this.comparatorService.orderByComparator(b.nummer, a.nummer));
      default:
        return list;
    }
  }

  private sortByComparator(list: GrundbuchamtListEntry[], sort: (a: string, b: string) => number) {
    return list.sort((a, b) => sort(this.translateNamePipe.transform(a.name), this.translateNamePipe.transform(b.name)));
  }
}
